/**
 * Text and Zoom Handler Utilities
 *
 * This file contains utility functions for text management and zoom functionality
 * extracted from MyCanvas.js to improve code organization and maintainability.
 */

import { logCanvasHScroll } from "./zoomViewportUtils";

/**
 * Add a new text element to the canvas
 * Returns a function that adds a text element using the current texts state
 * @param {Function} setTexts - Setter for texts array (accessed via callback)
 * @param {Function} setSelectedObject - Setter for selected object
 * @param {Function} setSelectedObjects - Setter for selected objects array
 * @param {Function} setSelectedImage - Setter for selected image
 * @param {Function} setSelectedArrow - Setter for selected arrow
 * @param {Function} setSelectedLine - Setter for selected line
 * @param {Function} setSelectedDoubleSidedArrow - Setter for selected double-sided arrow
 * @param {Function} setEditTextBoxContent - Setter for edit text box content state
 * @param {Function} setShowEditTask - Setter for show edit task state
 * @param {Function} setShowEditNote - Setter for show edit note state
 * @param {Function} setSelectedText - Setter for selected text
 * @param {Function} setSelectedTextId - Setter for selected text ID
 * @param {Function} setShowTextBox - Setter for show text box state
 * @param {Function} saveToHistory - Function to save to history
 * @param {Function} generateMarkerId - Function to generate unique IDs
 * @param {Object} transformerRef - Reference to transformer
 * @param {Object} pendingSelection - Reference to pending selection
 */
export const createAddText =
  (
    setTexts,
    setSelectedObject,
    setSelectedObjects,
    setSelectedImage,
    setSelectedArrow,
    setSelectedLine,
    setSelectedDoubleSidedArrow,
    setEditTextBoxContent,
    setShowEditTask,
    setShowEditNote,
    setSelectedText,
    setSelectedTextId,
    setShowTextBox,
    saveToHistory,
    generateMarkerId,
    transformerRef,
    pendingSelection,
    texts // Add texts as parameter to access current state directly
  ) =>
  () => {
    try {
      // Generate ID before creating the text object
      let textId;
      try {
        textId = generateMarkerId();
      } catch (error) {
        console.error("Error generating marker ID:", error);
        return;
      }

      if (!textId || typeof textId !== "string") {
        console.error("Invalid marker ID generated:", textId);
        return;
      }

      // Calculate coordinates
      const desiredCenterX = 200;
      const desiredCenterY = 200;
      const textWidth = 225;
      const textHeight = 100;
      const topLeftX = desiredCenterX - textWidth / 2;
      const topLeftY = desiredCenterY - textHeight / 2;

      // Calculate count from current texts array (matching working version exactly)
      const currentTexts = texts || [];
      const count = currentTexts.length + 1;

      // Create newText with correct count (matching working version exactly)
      // createdOnCanvas: true so save/leave includes this text even though id is marker-* (API only excludes template marker texts)
      const newText = {
        id: textId,
        name: `Text Box TB${count}`,
        x: topLeftX,
        y: topLeftY,
        part: "",
        task: "",
        note: "",
        unit: "",
        qty: 0,
        textStyles: {},
        selectedContent: null,
        showBackground: true,
        width: textWidth,
        height: textHeight,
        showContentInput: false,
        content: null,
        fontSize: 14,
        createdOnCanvas: true,
      };

      // Update texts array and clear selectedContent from all existing texts in one operation
      const updatedTexts = [...currentTexts, newText].map((t) => ({
        ...t,
        selectedContent: null,
      }));

      setTexts(updatedTexts);

      // Safety check: ensure newText was created successfully
      if (!newText || !newText.id) {
        console.error("Failed to create new text element", { newText, textId });
        return;
      }

      // Clear all previous selections first
      setSelectedObject(null);
      setSelectedObjects([]);
      setSelectedImage(null);
      setSelectedArrow(null);
      setSelectedLine(null);
      setSelectedDoubleSidedArrow(null);

      // Clear editing states
      setEditTextBoxContent(false);
      setShowEditTask(false);
      setShowEditNote(false);

      // Clear transformer
      if (transformerRef?.current) {
        transformerRef.current.nodes([]);
      }

      // Set text-specific selection states
      // Need to set both selectedText (for TextBoxWithButtons) and selectedObject (for transformer)
      setSelectedText(newText);
      setSelectedTextId(newText.id);
      setShowTextBox(true);

      // Save to history after adding text with the updated texts state
      // Pass newText override to ensure correct selectedText state in history
      if (saveToHistory && updatedTexts) {
        saveToHistory(
          "add_text",
          null,
          null,
          null,
          null,
          updatedTexts,
          newText,
          newText.id
        );
      }

      // Set selectedObject with delay to ensure DOM is rendered before transformer attaches
      setTimeout(() => {
        setSelectedObject({
          ...newText,
          type: "Text",
          selectedContent: null,
        });
      }, 10);
    } catch (error) {
      console.error("Error in addText function:", error);
    }
  };

/**
 * Update text content for a specific field
 * @param {string} field - Field to update
 * @param {any} value - New value
 * @param {string} selectedTextId - ID of selected text
 * @param {Function} setTexts - Setter for texts array
 * @param {Function} saveToHistory - Function to save to history
 */
export const createUpdateTextContent =
  (setTexts, saveToHistory) => (field, value, selectedTextId) => {
    setTexts((prevTexts) =>
      prevTexts.map((text) =>
        text.id === selectedTextId ? { ...text, [field]: value } : text
      )
    );

    // Save to history after updating text content
    saveToHistory("update_text_content");
  };

/**
 * Handle zoom functionality
 * @param {number} factor - Zoom factor (1.1 for zoom in, 0.9 for zoom out)
 * @param {number} zoomLevel - Current zoom level
 * @param {Function} setZoomLevel - Setter for zoom level
 * @param {Function} setZoomInputValue - Setter for zoom input value
 * @param {Function} saveToHistory - Function to save to history
 * @param {Object} scrollAreaRef - Reference to scroll area
 * @param {Object} scaledContentRef - Reference to scaled canvas wrapper (main stage)
 * @param {{ current: unknown }} stageRef - Main Konva stage ref (unused; kept for API stability)
 * @param {{ current: { left: number; maxL: number } | null } | null} preZoomHScrollRef - Filled before zoom to preserve horizontal scrollbar ratio
 */
export const createHandleZoom =
  (
    setZoomLevel,
    setZoomInputValue,
    saveToHistory,
    scrollAreaRef,
    scaledContentRef,
    stageRef,
    preZoomHScrollRef,
  ) =>
  (factor, zoomLevel, pointerOpts) => {
    try {
      const currentScale = zoomLevel || 1;

      // Convert to percentage, apply factor, then convert back to scale
      let currentPercent = Math.round(currentScale * 100);

      // Use the factor to determine direction: 1.1 = zoom in, 0.9 = zoom out
      if (factor > 1) {
        // Zoom in: add 10%
        currentPercent += 10;
      } else if (factor < 1) {
        // Zoom out: subtract 10%
        currentPercent -= 10;
      }

      // Convert back to scale and clamp between 10% and 500%
      let newScale = currentPercent / 100;
      newScale = Math.max(0.1, Math.min(newScale, 5));

      const zoomRatio = newScale / currentScale;

      // Zoom is handled by CSS transform on the canvas container
      setZoomLevel(newScale);
      setZoomInputValue(Math.round(newScale * 100)); // Update input value to match new zoom level

      if (zoomRatio !== 1 && preZoomHScrollRef) {
        logCanvasHScroll("handleZoom", {
          factor,
          currentScale,
          newScale,
          zoomRatio,
          pointer: pointerOpts?.fromWheel ? pointerOpts : undefined,
        });
        if (newScale !== currentScale) {
          if (
            pointerOpts?.fromWheel &&
            Number.isFinite(pointerOpts.clientX) &&
            Number.isFinite(pointerOpts.clientY)
          ) {
            preZoomHScrollRef.current = {
              mode: "pointer",
              clientX: pointerOpts.clientX,
              clientY: pointerOpts.clientY,
              oldScale: currentScale,
              newScale,
            };
          } else {
            preZoomHScrollRef.current = { mode: "center" };
          }
        }
      }

      saveToHistory();
    } catch (error) {}
  };
